import { useEffect, useState } from 'react';
import { type User } from 'firebase/auth';
import { auth, onAuthChanged } from '../services/firebase';
import LoginPage from '../pages/LoginPage';

const SESSION_KEY = 'raapid_auth_ts';

function touchSession() {
  localStorage.setItem(SESSION_KEY, String(Date.now()));
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
}

function isInIframe(): boolean {
  try { return window.self !== window.top; } catch { return true; }
}

export default function AuthGuard({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [ready, setReady] = useState(false);

  const [skipAuth] = useState(() => {
    const mode = new URLSearchParams(window.location.search).get('mode');
    if (mode === 'smart') return true;
    if (isInIframe()) return true;
    return false;
  });

  useEffect(() => {
    if (skipAuth) return;
    let mounted = true;

    // Wait for Firebase to fully hydrate auth state from IndexedDB,
    // THEN subscribe to changes. This prevents the spurious initial null
    // that fires before IndexedDB is read.
    auth.authStateReady().then(() => {
      if (!mounted) return;

      // Set initial state from the hydrated auth
      if (auth.currentUser) {
        setUser(auth.currentUser);
        touchSession();
      } else {
        setUser(null);
        clearSession();
      }
      setReady(true);

      // Now subscribe to FUTURE changes (login/logout)
      const unsub = onAuthChanged((u) => {
        if (u) {
          if (mounted) setUser(u);
          touchSession();
        } else {
          if (mounted) setUser(null);
          clearSession();
        }
      });

      // Store unsub so we can clean up
      cleanupRef = unsub;
    });

    let cleanupRef: (() => void) | null = null;
    return () => {
      mounted = false;
      cleanupRef?.();
    };
  }, [skipAuth]);

  if (skipAuth) return <>{children}</>;

  // Firebase resolved
  if (user) return <>{children}</>;

  // Still loading (authStateReady hasn't resolved yet)
  if (!ready) {
    return (
      <div style={{
        height: '100%',
        background: '#0B1120',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}>
        <img src={`${import.meta.env.BASE_URL}raapid-logo.png`} alt="RAAPID" style={{ height: '36px', marginBottom: '24px', filter: 'brightness(0) invert(1)' }} />
        <div style={{
          width: '28px', height: '28px',
          border: '3px solid #1E293B',
          borderTopColor: '#6366F1',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return <LoginPage />;
}
